import { ref, watch, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useAllPatientsFiltersStore } from "@/stores/allPatientsFiltersStore";

/**
 * Composable for managing filter state with URL query parameter persistence
 * for the All Patients page.
 *
 * Filters are synced bidirectionally:
 * - URL query params <-> Local component state <-> Pinia store
 *
 * On mount:
 * - If URL has params, use those (user navigated via URL or refresh)
 * - If URL is empty but store has values, restore from store (user navigated back)
 *
 * When filters change:
 * - URL is updated (enables refresh/sharing)
 * - Store is updated (enables navigation back with preserved filters)
 */

/**
 * Format a Date object to MM-DD-YYYY string for URL
 */
const formatDateForUrl = (date) => {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return null;
  const month = (d.getMonth() + 1).toString().padStart(2, "0");
  const day = d.getDate().toString().padStart(2, "0");
  const year = d.getFullYear();
  return `${month}-${day}-${year}`;
};

/**
 * Parse MM-DD-YYYY string from URL to Date object
 */
const parseDateFromUrl = (dateStr) => {
  if (!dateStr) return null;
  const parts = dateStr.split("-");
  if (parts.length !== 3) return null;
  const [month, day, year] = parts.map(Number);
  if (isNaN(month) || isNaN(day) || isNaN(year)) return null;
  const date = new Date(year, month - 1, day);
  if (isNaN(date.getTime())) return null;
  return date;
};

export function useAllPatientsFilters() {
  const router = useRouter();
  const route = useRoute();
  const filtersStore = useAllPatientsFiltersStore();

  // Filter state
  const selectedStatus = ref("ACTIVE");
  const patientSearchQuery = ref("");
  const dateFilter = ref(null);
  const sortBy = ref(null); // null means use default from client config

  // Flag to prevent recursive updates
  let isUpdatingFromUrl = false;

  /**
   * Initialize filters from URL query parameters or store
   */
  const initFiltersFromUrl = () => {
    isUpdatingFromUrl = true;

    const query = route.query;
    const hasUrlParams = query.status || query.patient || query.date || query.sort;

    if (hasUrlParams) {
      // URL has params - use those as source of truth
      if (query.status && ["ACTIVE", "ARCHIVED", "COMPLETED"].includes(query.status)) {
        selectedStatus.value = query.status;
      }

      if (query.patient) {
        patientSearchQuery.value = query.patient;
      }

      if (query.date) {
        dateFilter.value = parseDateFromUrl(query.date);
      }

      if (query.sort && ["newest", "oldest"].includes(query.sort)) {
        sortBy.value = query.sort;
      }

      // Sync to store
      syncToStore();
    } else if (filtersStore.hasActiveFilters) {
      // No URL params but store has values - restore from store
      selectedStatus.value = filtersStore.status;
      patientSearchQuery.value = filtersStore.patient;
      dateFilter.value = filtersStore.date;
      sortBy.value = filtersStore.sortBy;

      // Update URL to reflect restored filters
      updateUrlFromFilters();
    }

    isUpdatingFromUrl = false;
  };

  /**
   * Sync current filter state to the Pinia store
   */
  const syncToStore = () => {
    filtersStore.setFilters({
      status: selectedStatus.value,
      patient: patientSearchQuery.value,
      date: dateFilter.value,
      sortBy: sortBy.value,
    });
  };

  /**
   * Update URL query parameters from current filter state.
   * Only includes non-default values to keep URLs clean.
   */
  const updateUrlFromFilters = () => {
    if (isUpdatingFromUrl) return;

    const query = {};

    // Only include non-default values
    if (selectedStatus.value && selectedStatus.value !== "ACTIVE") {
      query.status = selectedStatus.value;
    }

    if (patientSearchQuery.value && patientSearchQuery.value.trim()) {
      query.patient = patientSearchQuery.value.trim();
    }

    const formattedDate = formatDateForUrl(dateFilter.value);
    if (formattedDate) {
      query.date = formattedDate;
    }

    if (sortBy.value) {
      query.sort = sortBy.value;
    }

    // Sync to store
    syncToStore();

    // Replace current URL without triggering navigation
    router.replace({ query });
  };

  /**
   * Build query params object for navigation to all-patients page.
   * Use this when navigating from other pages back to all-patients.
   */
  const buildFilterQuery = () => {
    const query = {};

    if (selectedStatus.value && selectedStatus.value !== "ACTIVE") {
      query.status = selectedStatus.value;
    }

    if (patientSearchQuery.value && patientSearchQuery.value.trim()) {
      query.patient = patientSearchQuery.value.trim();
    }

    const formattedDate = formatDateForUrl(dateFilter.value);
    if (formattedDate) {
      query.date = formattedDate;
    }

    if (sortBy.value) {
      query.sort = sortBy.value;
    }

    return query;
  };

  /**
   * Clear all filters and reset URL
   */
  const clearFilters = () => {
    selectedStatus.value = "ACTIVE";
    patientSearchQuery.value = "";
    dateFilter.value = null;
    sortBy.value = null;
    filtersStore.clearFilters();
    router.replace({ query: {} });
  };

  /**
   * Check if any non-default filters are active
   */
  const hasActiveFilters = () => {
    return (
      selectedStatus.value !== "ACTIVE" ||
      (patientSearchQuery.value && patientSearchQuery.value.trim()) ||
      dateFilter.value ||
      sortBy.value
    );
  };

  // Initialize from URL on mount
  onMounted(() => {
    initFiltersFromUrl();
  });

  // Watch for filter changes and update URL + store
  watch(selectedStatus, updateUrlFromFilters);
  watch(dateFilter, updateUrlFromFilters);
  // Note: patientSearchQuery is updated on explicit search action, not on every keystroke

  return {
    // Filter state (reactive refs)
    selectedStatus,
    patientSearchQuery,
    dateFilter,
    sortBy,

    // Methods
    updateUrlFromFilters,
    buildFilterQuery,
    clearFilters,
    hasActiveFilters,
    initFiltersFromUrl,
  };
}
